import React, { useCallback, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { notification } from 'antd';
import { Select, DatePicker, Form, Input, Alert } from 'antd';
import { BaseForm, Col, SubmitButton } from './PaymentDocuments.styled';
import SuccessModal from './Modal/SuccessModal';
import srv from '../../../services';

const { RangePicker } = DatePicker;

export default function PaymentDocuments() {
 const {
  handleCancel,
  documents,
  contracts,
  isLoading,
  visible,
  error,
  form,
  data,
  onFinish, // Явно достаем обработчики
  onValuesChange,
 } = usePaymentDocuments();

 return (
  <>
   <BaseForm
    layout="vertical"
    scrollToFirstError
    form={form}
    onFinish={onFinish}
    onValuesChange={onValuesChange}
   >
    <Col>
     <Form.Item
      name="contractId"
      label="№ Договора"
      rules={[{ required: true, message: 'Выберите № договора' }]}
     >
      <Select
       showSearch
       optionFilterProp="children"
       placeholder="Выберите № договора"
       loading={isLoading}
      >
       {contracts?.map((item) => (
        <Select.Option key={item.id} value={item.aisId}>
         {item.resources
          ? `${item.printNumber} - ${item.resources}`
          : item.printNumber}
        </Select.Option>
       ))}
      </Select>
     </Form.Item>
    </Col>
    <Col flex={1.3}>
     <Form.Item
      name="period"
      label="Период запроса"
      rules={[{ required: true, message: 'Выберите период запроса' }]}
     >
      <RangePicker allowClear format="DD.MM.YYYY" style={{ width: '100%' }} />
     </Form.Item>
     <Form.Item
      name="documentsType"
      label="Тип запрашиваемого документа"
      rules={[{ required: true, message: 'Выберите тип документа' }]}
     >
      <Select
       mode="multiple"
       showSearch
       optionFilterProp="children"
       placeholder="Выберите тип документа"
       loading={isLoading}
      >
       {documents?.map((item) => (
        <Select.Option key={item.id} value={item.id}>
         {item.name}
        </Select.Option>
       ))}
      </Select>
     </Form.Item>
    </Col>
    <Col flex={1.2}>
     <Form.Item
      name="eMail"
      label="E-mail"
      rules={[
       { required: true, message: 'Введите E-mail' },
       { type: 'email', message: 'Некорректный формат E-mail' },
      ]}
     >
      <Input placeholder="Введите E-mail" />
     </Form.Item>

     {!!error && <Alert type="error" showIcon message={error} />}

     {/* htmlType="submit" автоматически триггерит onFinish формы без костылей с onClick */}
     <SubmitButton disabled={isLoading} loading={isLoading} htmlType="submit">
      Запросить
     </SubmitButton>
    </Col>
   </BaseForm>
   <SuccessModal visible={visible} handleCancel={handleCancel} data={data} />
  </>
 );
}

function usePaymentDocuments() {
 const queryClient = useQueryClient();
 const contracts = queryClient.getQueryData<any[]>('contracts');
 const rawDocuments = queryClient.getQueryData<any[]>('documents');
 const [form] = Form.useForm();

 const [visible, setVisible] = useState(false);

 // Исправлено: useMemo защищает глобальный кэш React Query от неконтролируемого замусоривания .push()
 const documents = useMemo(() => {
  if (!rawDocuments || rawDocuments.length === 0) return rawDocuments;
  return rawDocuments;
 }, [rawDocuments]);

 const showModal = () => setVisible(true);
 const handleCancel = () => setVisible(false);

 const {
  data,
  mutate: save,
  isLoading,
  error,
  reset,
 } = useMutation(srv.requestDocuments, {
  onSuccess: () => {
   form.resetFields();
   showModal();
  },
 });

 const onFinish = useCallback(
  (values: any) => {
   // Читаем email напрямую из переданных формой значений values.eMail
   const emailValue = values.eMail
    ? String(values.eMail).toLowerCase().trim()
    : '';

   // Регулярное выражение для точной проверки
   const emailRegex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

   if (emailRegex.test(emailValue)) {
    save({
     contractId: values.contractId,
     eMail: emailValue,
     documentsType: values.documentsType,
     startDate: values.period?.[0],
     endDate: values.period?.[1],
    });
   } else {
    notification.error({
     message: 'Неверный адрес электронной почты',
     placement: 'top',
     duration: 7,
    });
   }
  },
  [save],
 );

 const onValuesChange = useCallback(() => {
  if (error) reset();
 }, [error, reset]);

 return {
  onFinish,
  onValuesChange,
  handleCancel,
  data,
  contracts,
  isLoading,
  form,
  error,
  documents,
  visible,
 };
}
